/**
 * @author Martin Leonov <martin.leonov@ki-wi.cz>
 * @copyright Ki-Wi Digital s.r.o.
 * @license
 * All Rights Reserved.
 * Unauthorized copying of this file is strictly prohibited.
 * Proprietary and confidential.
 */

// @ts-check

/* eslint-env browser */
/* eslint-disable no-await-in-loop */
/* eslint-disable max-statements */

import FileSystemPathFactory from '../file/system/file-system-path-factory';
import FileSystemUtil from '../file/system/file-system-util';
import { hasAsset } from '../medium/medium';
import PromiseUtil from '../promise/promise-util';
import Queue from '../queue/queue';

/**
 * @import { Asset } from '../asset/asset'
 * @import BasePathResolver from '../basepath/basepath'
 * @import { ContentInfo } from '../content/info/content-info'
 * @import { FileSystemEntry, FileSystemManager, FileSystemUnit } from '../file/system'
 * @import FileSystemPath from '../file/system/file-system-path'
 * @import { Logger } from '../logger/logger'
 * @import { LogicalUnitMapper } from '../logical-unit-mapper'
 * @import Store from '../store/store'
 */

/**
 * @typedef {Pick<ContentInfo, 'media'>} IContentInfo
 * @typedef {{ files: FileSystemEntry[]; errors: unknown[] }} RemovedFilesInfo
 * @typedef {{ assets: Asset[]; errors: unknown[] }} RemovedAssetsInfo
 * @typedef {{ assets: Asset[]; files: FileSystemEntry[] }} CleanerInfo
 */

/**
 * Čistič místa na disku.
 * Snaží se zajistit dostatek volného místa pro nový obsah.
 */
export default class Cleaner {
  /**
   * @param {BasePathResolver} basePathResolver Mapovač logických cest na fyzické.
   * @param {FileSystemManager} fileSystemManager Správce souborového systému.
   * @param {Logger} logger Logger pro logování událostí.
   * @param {LogicalUnitMapper} logicalUnitMapper Mapovač logických jednotek.
   * @param {Store} store Perzistentní read-only datový sklad s assety.
   */
  constructor(
    basePathResolver,
    fileSystemManager,
    logger,
    logicalUnitMapper,
    store
  ) {
    this.basePathResolver = basePathResolver;
    this.fileSystemManager = fileSystemManager;
    this.logger = logger;
    this.logicalUnitMapper = logicalUnitMapper;
    this.store = store;

    this.mutex = new Queue();
  }

  /**
   * Pokusí se vyčistit místo na disku pro nový obsah. Tato metoda vždy uspěje.
   * @param {IContentInfo} contentInfo Informace o novém obsahu zařízení.
   * @returns {Promise<CleanerInfo>} Informace o vyčištěných datových modelech.
   */
  async clean(contentInfo) {
    return await this.mutex.enqueue(async () => {
      try {
        const units = await FileSystemUtil.findUsedLogicalUnits(
          this.fileSystemManager,
          this.logicalUnitMapper,
          this.store
        );
        const logicalPaths = await PromiseUtil.allParallel(
          units.map((unit) => () => this.basePathResolver.resolve(unit))
        );
        // Seznam assetů, které potřebuji pro aktuální contentInfo.
        const currentAssets = [...contentInfo.media.values()]
          .filter(hasAsset)
          .map((medium) => medium.asset);

        // Smaže všechny záznamy assetů v localstorage, které se nepoužívají.
        const removedAssets = await this.cleanAssets(currentAssets).then(
          ({ assets, errors }) => {
            this.handleErrors(errors, 'clean some assets from store');
            return assets;
          }
        );
        // Smaže všechny soubory, které nemají vazbu na assety.
        const excludedPaths = currentAssets.map((asset) => asset.path);
        const removedFiles = await this.cleanLogicalPaths(
          logicalPaths,
          excludedPaths
        ).then(({ files, errors }) => {
          this.handleErrors(errors, 'clean some of used logical units');
          return files;
        });

        // Po odstranění souborů chvíli trvá, než se tato informace projeví při
        // získávání volného místa.
        await PromiseUtil.wait(1000);

        return {
          assets: removedAssets,
          files: removedFiles
        };
      } catch (cause) {
        this.logger.error(new Error('failed to clean storage', { cause }));
        return { assets: [], files: [] };
      }
    });
  }

  /**
   * Smaže stažený obsah na všech dostupných jednotkách.
   * @returns {Promise<void>}
   */
  async cleanAll() {
    return await this.mutex.enqueue(async () => {
      try {
        // Smaže všechny záznamy assetů v localstorage.
        await this.store.destroyAllAsset();
      } catch (cause) {
        this.logger.error(new Error('store.destroyAllAsset failed', { cause }));
      }

      const fileSystemUnits = await this.fileSystemManager.getAllUnits();
      const logicalPaths = await fileSystemUnitsToLogicalPaths(
        this.basePathResolver,
        fileSystemUnits,
        this.logicalUnitMapper
      );
      await this.cleanLogicalPaths(logicalPaths).then(({ errors }) => {
        this.handleErrors(errors, 'clean some of all logical units');
      });
    });
  }

  /**
   * Smaže všechny záznamy assetů v localstorage, které se nepoužívají. Případné chyby při odstraňování
   * jsou vráceny. Ostatní chyby jsou převyhozeny. Metoda vždy uspěje.
   * @private
   * @param {Asset[]} currentAssets
   * @returns {Promise<RemovedAssetsInfo>}
   */
  async cleanAssets(currentAssets) {
    /** @type {RemovedAssetsInfo} */
    const removedAssetsInfo = { errors: [], assets: [] };
    try {
      const allAssets = await this.store.findAllAsset();
      const currAssetIdsSet = new Set(currentAssets.map((asset) => asset.id));

      // Výběr všech assetů z localstorate, které nepatří k aktuálně používaným assetům.
      const assetsToRemove = allAssets.filter(
        (asset) => !currAssetIdsSet.has(asset.id)
      );
      // Smaže pouze všechny nepotřebné záznamy v localstorage.
      const removedAssets = await PromiseUtil.allSerial(
        assetsToRemove.map((a) => async () => {
          try {
            await this.store.destroyAsset(a.id);
            return a;
          } catch (err) {
            removedAssetsInfo.errors.push(err);
            return undefined;
          }
        })
      );
      removedAssetsInfo.assets.push(
        ...removedAssets.filter((asset) => asset !== undefined)
      );
    } catch (cause) {
      removedAssetsInfo.errors.push(
        new Error('failed to clean assets from store', { cause })
      );
    }

    return removedAssetsInfo;
  }

  /**
   * Smaže všehny nepotřebné soubory v cílové cestě. Případné chyby jsou vráceny.
   * @private
   * @param {FileSystemPath} logicalPath Cesta ve které se provést čištění.
   * @param {string[]} [excludedLogicalPaths] Cesty, které se při čištění mají vynechat (nebudou smazány).
   * @returns {Promise<RemovedFilesInfo>} Informace o úspěšně odebraných souborech a případných chybách.
   */
  async cleanLogicalPath(logicalPath, excludedLogicalPaths = []) {
    const allEntries = await FileSystemUtil.listDirectory(
      this.fileSystemManager,
      await this.basePathResolver.toPhysicalPath(logicalPath)
    );
    const excludedPhysicalPaths = await PromiseUtil.allParallel(
      excludedLogicalPaths.map(
        (excludedLogicalPath) => () =>
          this.basePathResolver.toPhysicalPath(
            FileSystemPathFactory.fromValue(excludedLogicalPath)
          )
      )
    ).then((paths) => new Set(paths.map((path) => path.getValue())));

    /** @type {unknown[]} */
    const errors = [];
    const removePromises = allEntries.map((entry) => async () => {
      if (excludedPhysicalPaths.has(entry.getPath().getValue())) {
        return;
      }
      try {
        await entry.remove({ recursive: true });
        return entry;
      } catch (err) {
        // V tomto případě nechceme posílat stack trace.
        /** @type {Error} */ (err).stack = '';
        errors.push(err);
        return undefined;
      }
    });
    return PromiseUtil.allSerial(removePromises).then((files) => ({
      files: files.filter((file) => file !== undefined),
      errors
    }));
  }

  /**
   * Smaže všechny nepotřebné soubory v zadaných logických cestách. Případné chyby jsou vráceny.
   * Metoda vždy uspěje.
   * @private
   * @param {FileSystemPath[]} logicalPaths
   * @param {string[]} [excludedLogicalPaths]
   * @returns {Promise<RemovedFilesInfo>}
   */
  async cleanLogicalPaths(logicalPaths, excludedLogicalPaths = []) {
    /** @type {RemovedFilesInfo} */
    const removedFilesInfo = { errors: [], files: [] };
    await PromiseUtil.allSerial(
      logicalPaths.map((logicalPath) => async () => {
        try {
          const info = await this.cleanLogicalPath(
            logicalPath,
            excludedLogicalPaths
          );
          removedFilesInfo.files.push(...info.files);
          removedFilesInfo.errors.push(...info.errors);
        } catch (cause) {
          removedFilesInfo.errors.push(
            new Error(
              `cleanLogicalPath failed for "${logicalPath.getValue()}"`,
              { cause }
            )
          );
        }
      })
    );
    return removedFilesInfo;
  }

  /**
   * @private
   * @param {unknown[]} errors
   * @param {string} message
   */
  handleErrors(errors, message) {
    if (errors.length > 0) {
      this.logger.error(new AggregateError(errors, message));
    }
  }
}

/**
 * @param {BasePathResolver} basePathResolver
 * @param {FileSystemUnit[]} fileSystemUnits
 * @param {LogicalUnitMapper} logicalUnitMapper
 * @returns {Promise<FileSystemPath[]>}
 */
async function fileSystemUnitsToLogicalPaths(
  basePathResolver,
  fileSystemUnits,
  logicalUnitMapper
) {
  return await PromiseUtil.allSerial(
    fileSystemUnits.map((fileSystemUnit) => async () => {
      const logicalUnit = await logicalUnitMapper.toLogical(fileSystemUnit);
      return await basePathResolver.resolve(logicalUnit);
    })
  );
}
